import { useEffect, useState, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        setError(null);
      }
    );

    // THEN check for existing session
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!mounted) return;
        
        if (error) {
          console.error("Auth session error:", error);
          setError(error);
          // Still set loading to false - allow app to continue
        }
        
        setSession(session);
        setUser(session?.user ?? null);
      } catch (err) {
        console.error("Auth init exception:", err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error("Auth initialization failed"));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Sign out error:", error);
        // Force clear local state even if server call fails
      }
      setSession(null);
      setUser(null);
    } catch (err) {
      console.error("Sign out exception:", err);
      // Force clear local state
      setSession(null);
      setUser(null);
    }
  }, []);

  return {
    user,
    session,
    isLoading,
    error,
    signOut,
    isAuthenticated: !!session,
  };
}
